import { formatHoursToHM } from '@/composable/useFormatDateAndHours'
import { translateAlarmType } from '@/interfaces/enums'
import {
  IAlarmAdherenceByShift,
  IAlarmByClassification,
  IAlarmByGroup,
  IAlarmByNature,
  IAlarmBySensor,
  IAlarmByShift,
  IAlarmByType,
  IClassificationMetrics,
} from '@/interfaces/stores/alarm'
import { IAlarmDurationByType } from '@/interfaces/stores/alarm'

const COMPACT_DONUT_HEIGHT = 340
const COMPACT_BAR_HEIGHT = 260

/** Paleta do sistema (tailwind) — 34 cores distintas antes de reutilização com variação. */
const DONUT_PALETTE = [
  '#123ADD',
  '#0eca8b',
  '#FB5454',
  '#04304c',
  '#80CAEE',
  '#8b5cf6',
  '#D97706',
  '#042194',
  '#099657',
  '#3b82f6',
  '#E33D3D',
  '#c084fc',
  '#FACC15',
  '#667085',
  '#1d4ed8',
  '#BF1F1F',
  '#2563eb',
  '#344054',
  '#EAB308',
  '#991616',
  '#475467',
  '#730D0D',
  '#101828',
  '#98A2B3',
  '#64748B',
  '#333A48',
  '#000A1E',
  '#ec4899',
  '#f97316',
  '#14b8a6',
  '#06b6d4',
  '#84cc16',
  '#a855f7',
  '#22d3ee',
]

const DONUT_LEGEND_MARKERS = {
  shape: 'square' as const,
  width: 10,
  height: 10,
  radius: 0,
}

const DONUT_REUSE_BRIGHTNESS_STEP = 0.2

function buildDonutPalette(sliceCount: number): string[] {
  if (sliceCount <= 0) return [...DONUT_PALETTE]

  const colors: string[] = []
  for (let i = 0; i < sliceCount; i++) {
    if (i < DONUT_PALETTE.length) {
      colors.push(DONUT_PALETTE[i])
      continue
    }

    const base = DONUT_PALETTE[i % DONUT_PALETTE.length]
    const cycle = Math.floor(i / DONUT_PALETTE.length)
    const darken = cycle % 2 === 1
    colors.push(adjustHexBrightness(base, darken ? -DONUT_REUSE_BRIGHTNESS_STEP : DONUT_REUSE_BRIGHTNESS_STEP))
  }

  return colors
}

/** Escurece (fator negativo) ou clareia (fator positivo) um hex via ajuste linear nos canais RGB. */
function adjustHexBrightness(hex: string, factor: number): string {
  const normalized = hex.replace('#', '')
  const r = Number.parseInt(normalized.slice(0, 2), 16)
  const g = Number.parseInt(normalized.slice(2, 4), 16)
  const b = Number.parseInt(normalized.slice(4, 6), 16)

  const adjust = (channel: number) => {
    if (factor < 0) return Math.max(0, Math.round(channel * (1 + factor)))
    return Math.min(255, Math.round(channel + (255 - channel) * factor))
  }

  const toHex = (value: number) => value.toString(16).padStart(2, '0')
  return `#${toHex(adjust(r))}${toHex(adjust(g))}${toHex(adjust(b))}`
}

function applyDonutVisualOptions(
  chartOptionsParams: Record<string, unknown>,
  seriesParams: unknown[],
): Record<string, unknown> {
  const sliceCount = Array.isArray(seriesParams) ? seriesParams.length : 0
  const legend = (chartOptionsParams.legend ?? {}) as Record<string, unknown>

  return {
    ...chartOptionsParams,
    colors: buildDonutPalette(sliceCount),
    legend: {
      ...legend,
      markers: DONUT_LEGEND_MARKERS,
    },
  }
}

function donutCountTooltip(seriesParams: number[]) {
  const total = seriesParams.reduce((sum, value) => sum + value, 0)

  return {
    enabled: true,
    fillSeriesColor: false,
    y: {
      formatter: (val: number) => {
        const pct = total > 0 ? ((val / total) * 100).toFixed(1) : '0.0'
        return `${val} alarmes (${pct}%)`
      },
    },
  }
}

function donutDurationTooltip(seriesParams: number[]) {
  const total = seriesParams.reduce((sum, value) => sum + value, 0)

  return {
    enabled: true,
    fillSeriesColor: false,
    y: {
      formatter: (val: number) => {
        const pct = total > 0 ? ((val / total) * 100).toFixed(1) : '0.0'
        return `${val.toFixed(1)} h (${pct}%)`
      },
    },
  }
}

function barHoursTooltip() {
  return {
    enabled: true,
    y: {
      formatter: (value: number) => formatHoursToHM(value),
    },
  }
}

function barPercentTooltip() {
  return {
    enabled: true,
    y: {
      formatter: (value: number) => `${value}%`,
    },
  }
}

const BAR_DATA_LABEL_STYLE = {
  fontSize: '11px',
  fontFamily: 'Satoshi, sans-serif',
  fontWeight: '600',
  colors: ['#344054'],
}

function barHoursDataLabels() {
  return {
    enabled: true,
    offsetY: -18,
    style: BAR_DATA_LABEL_STYLE,
    background: { enabled: false },
    formatter: (value: number) => (value > 0 ? formatHoursToHM(value) : '0h'),
  }
}

function barPercentDataLabels() {
  return {
    enabled: true,
    offsetY: -18,
    style: BAR_DATA_LABEL_STYLE,
    background: { enabled: false },
    formatter: (value: number) => `${Math.round(value)}%`,
  }
}

function applyCompactChartOptions(chartOptionsParams: Record<string, unknown>, compact: boolean): Record<string, unknown> {
  if (!compact) return chartOptionsParams

  const plotOptions = chartOptionsParams.plotOptions as Record<string, unknown> | undefined
  const pie = plotOptions?.pie as Record<string, unknown> | undefined
  const legend = chartOptionsParams.legend as Record<string, unknown> | undefined

  const nextPlotOptions =
    plotOptions && pie ? { ...plotOptions, pie: { ...pie, customScale: 0.95 } } : plotOptions

  return {
    ...chartOptionsParams,
    legend: legend ? { ...legend, fontSize: '12px', offsetY: 4 } : legend,
    ...(nextPlotOptions ? { plotOptions: nextPlotOptions } : {}),
  }
}

function isBarChartType(chartId: string): boolean {
  return (
    chartId === 'alarm-report-average-hours-by-classification-chart' ||
    chartId === 'alarm-report-total-hours-by-classification-chart' ||
    chartId.startsWith('alarm-adherence-shift-')
  )
}

function isRoseChartType(chartId: string): boolean {
  return chartId === 'alarm-report-by-nature-chart'
}

export const identifyAlarmReportChart = (chartId: string): Function => {
  switch (chartId) {
    case 'alarm-report-by-sensor-chart':
      return formatAlarmReportBySensorChartParams
    case 'alarm-report-by-group-chart':
      return formatAlarmReportByGroupChartParams
    case 'alarm-report-by-type-chart':
      return formatAlarmReportByTypeChartParams
    case 'alarm-report-by-classification-chart':
      return formatAlarmReportByClassificationChartParams
    case 'alarm-report-by-nature-chart':
      return formatAlarmReportByNatureChartParams
    case 'alarm-report-average-hours-by-classification-chart':
      return formatAlarmReportAverageHoursByClassificationChartParams
    case 'alarm-report-total-hours-by-classification-chart':
      return formatAlarmReportTotalHoursByClassificationChartParams
    case 'alarm-duration-by-type-chart':
      return formatAlarmDurationByTypeChartParams
    case 'alarm-report-by-shift-chart':
      return formatAlarmReportByShiftChartParams
    case 'alarm-adherence-shift-classification-rate-bar':
      return formatAlarmAdherenceShiftClassificationRateBarParams
    case 'alarm-adherence-shift-avg-hours-bar':
      return formatAlarmAdherenceShiftAvgHoursBarParams
    case 'alarm-adherence-shift-sla-bar':
      return formatAlarmAdherenceShiftSlaBarParams
    default:
      return formatAlarmReportBySensorChartParams
  }
}

export const formatAlarmReportBySensorChartParams = (alarmsBySensor: IAlarmBySensor[]) => {
  const seriesParams: number[] = alarmsBySensor.map((metric) => metric.alarm_count)
  const totalAlarms = seriesParams.reduce((sum, value) => sum + value, 0)

  const chartOptionsParams = {
    labels: alarmsBySensor.map((metric) => metric.sensor_name),
    chart: {
      id: 'alarm-report-by-sensor-chart',
      type: 'donut',
      fontFamily: 'Satoshi, sans-serif',
    },
    legend: {
      show: true,
      fontSize: '14px',
      fontWeight: 'bold',
      labels: {
        useSeriesColors: true,
      },
      position: 'bottom',
    },
    plotOptions: {
      pie: {
        customScale: 0.9,
        donut: {
          size: '60%',
          labels: {
            show: true,
            name: {
              show: true,
            },
            value: {
              show: true,
              fontSize: '16px',
              fontWeight: 'bold',
            },
            total: {
              show: true,
              label: 'Total',
              formatter: () => `${totalAlarms} alarmes`,
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: donutCountTooltip(seriesParams),
  }

  return { seriesParams, chartOptionsParams }
}

export const formatAlarmReportByGroupChartParams = (alarmsByGroup: IAlarmByGroup[]) => {
  const seriesParams: number[] = alarmsByGroup.map((metric) => metric.alarm_count)
  const totalAlarms = seriesParams.reduce((sum, value) => sum + value, 0)

  const chartOptionsParams = {
    labels: alarmsByGroup.map((metric) => metric.group_name),
    chart: {
      id: 'alarm-report-by-group-chart',
      type: 'donut',
      fontFamily: 'Satoshi, sans-serif',
    },
    legend: {
      show: true,
      fontSize: '14px',
      fontWeight: 'bold',
      labels: {
        useSeriesColors: true,
      },
      position: 'bottom',
    },
    plotOptions: {
      pie: {
        customScale: 0.9,
        donut: {
          size: '60%',
          labels: {
            show: true,
            name: {
              show: true,
            },
            value: {
              show: true,
              fontSize: '16px',
              fontWeight: 'bold',
            },
            total: {
              show: true,
              label: 'Total',
              formatter: () => `${totalAlarms} alarmes`,
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: donutCountTooltip(seriesParams),
  }

  return { seriesParams, chartOptionsParams }
}

export const formatAlarmReportByShiftChartParams = (alarmsByShift: IAlarmByShift[]) => {
  const seriesParams: number[] = alarmsByShift.map((metric) => metric.alarm_count)
  const totalAlarms = seriesParams.reduce((sum, value) => sum + value, 0)

  const chartOptionsParams = {
    labels: alarmsByShift.map((metric) => metric.shift_name),
    chart: {
      id: 'alarm-report-by-shift-chart',
      type: 'donut',
      fontFamily: 'Satoshi, sans-serif',
    },
    legend: {
      show: true,
      fontSize: '14px',
      fontWeight: 'bold',
      labels: {
        useSeriesColors: true,
      },
      position: 'bottom',
    },
    plotOptions: {
      pie: {
        customScale: 0.9,
        donut: {
          size: '60%',
          labels: {
            show: true,
            name: {
              show: true,
            },
            value: {
              show: true,
              fontSize: '16px',
              fontWeight: 'bold',
            },
            total: {
              show: true,
              label: 'Total',
              formatter: () => `${totalAlarms} alarmes`,
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: donutCountTooltip(seriesParams),
  }

  return { seriesParams, chartOptionsParams }
}

export const formatAlarmReportByTypeChartParams = (alarmsByType: IAlarmByType[]) => {
  const seriesParams: number[] = alarmsByType.map((metric) => metric.alarm_count)
  const totalAlarms = seriesParams.reduce((sum, value) => sum + value, 0)

  const chartOptionsParams = {
    labels: alarmsByType.map((metric) => translateAlarmType(metric.type_name)),
    chart: {
      id: 'alarm-report-by-type-chart',
      type: 'donut',
      fontFamily: 'Satoshi, sans-serif',
    },
    legend: {
      show: true,
      fontSize: '14px',
      fontWeight: 'bold',
      labels: {
        useSeriesColors: true,
      },
      position: 'bottom',
    },
    plotOptions: {
      pie: {
        customScale: 0.9,
        donut: {
          size: '60%',
          labels: {
            show: true,
            name: {
              show: true,
            },
            value: {
              show: true,
              fontSize: '16px',
              fontWeight: 'bold',
            },
            total: {
              show: true,
              label: 'Total',
              formatter: () => `${totalAlarms} alarmes`,
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: donutCountTooltip(seriesParams),
  }

  return { seriesParams, chartOptionsParams }
}

export const formatAlarmReportByClassificationChartParams = (alarmsByClassification: IAlarmByClassification[]) => {
  const seriesParams: number[] = alarmsByClassification.map((metric) => metric.alarm_count)
  const totalAlarms = seriesParams.reduce((sum, value) => sum + value, 0)

  const chartOptionsParams = {
    labels: alarmsByClassification.map((metric) => metric.classification),
    chart: {
      id: 'alarm-report-by-classification-chart',
      type: 'donut',
      fontFamily: 'Satoshi, sans-serif',
    },
    legend: {
      show: true,
      fontSize: '14px',
      fontWeight: 'bold',
      labels: {
        useSeriesColors: true,
      },
      position: 'bottom',
    },
    plotOptions: {
      pie: {
        customScale: 0.9,
        expandOnClick: false, // Evita que os setores aumentem ao clicar
        donut: {
          size: '55%', // Reduzindo um pouco para dar mais espaço aos labels
          labels: {
            show: true,
            name: {
              show: true,
              offsetY: -10, // Move o nome para cima
            },
            value: {
              show: true,
              fontSize: '16px',
              fontWeight: 'bold',
              offsetY: 10, // Move o valor para baixo
            },
            total: {
              show: true,
              label: 'Total',
              formatter: () => `${totalAlarms} alarmes`,
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: donutCountTooltip(seriesParams),
    stroke: {
      width: 2,
      colors: ['#ffffff'],
    },
  }

  return { seriesParams, chartOptionsParams }
}
export const formatAlarmReportByNatureChartParams = (alarmsByNature: IAlarmByNature[]) => {
  const seriesParams: number[] = alarmsByNature.map((metric) => metric.alarm_count)
  const totalAlarms = seriesParams.reduce((sum, value) => sum + value, 0)

  const natureColors: Record<string, string> = {
    accidental: '#FB5454',
    scheduled: '#0eca8b',
    no_demand: '#667085',
    producing: '#123ADD',
    none: '#98A2B3',
  }

  const chartOptionsParams = {
    labels: alarmsByNature.map((metric) => metric.nature_label),
    colors: alarmsByNature.map((metric) => natureColors[metric.nature ?? 'none'] ?? natureColors.none),
    chart: {
      id: 'alarm-report-by-nature-chart',
      type: 'polarArea',
      fontFamily: 'Satoshi, sans-serif',
    },
    legend: {
      show: true,
      fontSize: '14px',
      fontWeight: 'bold',
      labels: {
        colors: alarmsByNature.map((metric) => natureColors[metric.nature ?? 'none'] ?? natureColors.none),
      },
      markers: { shape: 'circle', fillColors: alarmsByNature.map((metric) => natureColors[metric.nature ?? 'none'] ?? natureColors.none) },
      position: 'bottom',
    },
    plotOptions: {
      polarArea: {
        rings: {
          strokeWidth: 1,
        },
        spokes: {
          strokeWidth: 1,
        },
      },
    },
    stroke: {
      width: 2,
      colors: ['#ffffff'],
    },
    fill: {
      opacity: 0.9,
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => `${val.toFixed(1)}%`,
      style: {
        fontSize: '11px',
        fontWeight: '600',
        fontFamily: 'Satoshi, sans-serif',
        colors: ['#344054'],
      },
    },
    tooltip: donutCountTooltip(seriesParams),
  }

  return { seriesParams, chartOptionsParams }
}
export const formatAlarmDurationByTypeChartParams = (alarmDurationByType: IAlarmDurationByType[]) => {
  const seriesParams: number[] = alarmDurationByType.map((metric) => metric.total_duration_hours)
  const totalDuration = seriesParams.reduce((sum, value) => sum + value, 0)

  const chartOptionsParams = {
    labels: alarmDurationByType.map((metric) => translateAlarmType(metric.type_name)),

    chart: {
      id: 'alarm-duration-by-type-chart',
      type: 'donut',
      fontFamily: 'Satoshi, sans-serif',
    },
    legend: {
      show: true,
      fontSize: '14px',
      fontWeight: 'bold',
      labels: {
        useSeriesColors: true,
      },
      position: 'bottom',
    },
    plotOptions: {
      pie: {
        customScale: 0.9,
        donut: {
          size: '60%',
          labels: {
            show: true,
            name: {
              show: true,
            },
            value: {
              show: true,
              fontSize: '16px',
              fontWeight: 'bold',
            },
            total: {
              show: true,
              label: 'Total',
              formatter: () => `${totalDuration.toFixed(1)} h`,
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: donutDurationTooltip(seriesParams),
    stroke: {
      width: 2,
      colors: ['#ffffff'],
    },
  }

  return { seriesParams, chartOptionsParams }
}

export const formatAlarmReportAverageHoursByClassificationChartParams = (classificationMetrics: IClassificationMetrics[]) => {
  const seriesParams: Array<{ name: string; data: Array<number> }> = [{ name: 'Média de horas', data: [] }]
  let chartOptionsParams: object = {}

  classificationMetrics.forEach((reading) => {
    seriesParams[0].data.push(reading.average_duration_hours)
  })

  chartOptionsParams = {
    plotOptions: {
      bar: {
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: barHoursDataLabels(),
    legend: {
      show: false,
    },
    chart: {
      id: 'alarm-report-average-hours-by-classification-chart',
      type: 'bar',
      fontFamily: 'Satoshi, sans-serif',
      toolbar: {
        show: true,
        tools: {
          download: false,
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (value: number) => {
          return formatHoursToHM(value)
        },
      },
    },
    xaxis: {
      categories: classificationMetrics.map((metric) => metric.classification),
    },
    tooltip: barHoursTooltip(),
  }

  return { seriesParams, chartOptionsParams }
}

export const formatAlarmReportTotalHoursByClassificationChartParams = (classificationMetrics: IClassificationMetrics[]) => {
  const seriesParams: Array<{ name: string; data: Array<number> }> = [{ name: 'Total de horas', data: [] }]
  let chartOptionsParams: object = {}

  classificationMetrics.forEach((reading) => {
    seriesParams[0].data.push(reading.total_duration_hours)
  })

  chartOptionsParams = {
    plotOptions: {
      bar: {
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: barHoursDataLabels(),
    legend: {
      show: false,
    },
    chart: {
      id: 'alarm-report-total-hours-by-classification-chart',
      type: 'bar',
      fontFamily: 'Satoshi, sans-serif',
      toolbar: {
        show: true,
        tools: {
          download: false,
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (value: number) => {
          return formatHoursToHM(value)
        },
      },
    },
    xaxis: {
      categories: classificationMetrics.map((metric) => metric.classification),
    },
    tooltip: barHoursTooltip(),
  }

  return { seriesParams, chartOptionsParams }
}

export const formatAlarmAdherenceShiftClassificationRateBarParams = (rows: IAlarmAdherenceByShift[]) => {
  const seriesParams: Array<{ name: string; data: Array<number> }> = [{ name: 'Taxa de classificação', data: [] }]
  rows.forEach((row) => {
    seriesParams[0].data.push(Number((row.classification_rate * 100).toFixed(1)))
  })
  const chartOptionsParams = {
    plotOptions: {
      bar: {
        dataLabels: { position: 'top' },
      },
    },
    dataLabels: barPercentDataLabels(),
    legend: { show: false },
    chart: {
      id: 'alarm-adherence-shift-classification-rate-bar',
      type: 'bar',
      fontFamily: 'Satoshi, sans-serif',
      toolbar: { show: true, tools: { download: false } },
    },
    yaxis: {
      max: 115,
      labels: { formatter: (value: number) => `${Math.round(value)}%` },
    },
    xaxis: { categories: rows.map((r) => r.shift_name) },
    tooltip: barPercentTooltip(),
  }
  return { seriesParams, chartOptionsParams }
}

export const formatAlarmAdherenceShiftAvgHoursBarParams = (rows: IAlarmAdherenceByShift[]) => {
  const seriesParams: Array<{ name: string; data: Array<number> }> = [{ name: 'Tempo médio', data: [] }]
  rows.forEach((row) => {
    seriesParams[0].data.push(row.average_hours_to_classify ?? 0)
  })
  const chartOptionsParams = {
    plotOptions: {
      bar: {
        dataLabels: { position: 'top' },
      },
    },
    dataLabels: barHoursDataLabels(),
    legend: { show: false },
    chart: {
      id: 'alarm-adherence-shift-avg-hours-bar',
      type: 'bar',
      fontFamily: 'Satoshi, sans-serif',
      toolbar: { show: true, tools: { download: false } },
    },
    yaxis: {
      labels: {
        formatter: (value: number) => formatHoursToHM(value),
      },
    },
    xaxis: { categories: rows.map((r) => r.shift_name) },
    tooltip: barHoursTooltip(),
  }
  return { seriesParams, chartOptionsParams }
}

export const formatAlarmAdherenceShiftSlaBarParams = (rows: IAlarmAdherenceByShift[]) => {
  const seriesParams: Array<{ name: string; data: Array<number> }> = [{ name: 'Aderência ao SLA', data: [] }]
  rows.forEach((row) => {
    const pct = row.sla_compliance_rate != null ? row.sla_compliance_rate * 100 : 0
    seriesParams[0].data.push(Number(pct.toFixed(1)))
  })
  const chartOptionsParams = {
    plotOptions: {
      bar: {
        dataLabels: { position: 'top' },
      },
    },
    dataLabels: barPercentDataLabels(),
    legend: { show: false },
    chart: {
      id: 'alarm-adherence-shift-sla-bar',
      type: 'bar',
      fontFamily: 'Satoshi, sans-serif',
      toolbar: { show: true, tools: { download: false } },
    },
    yaxis: {
      max: 115,
      labels: { formatter: (value: number) => `${Math.round(value)}%` },
    },
    xaxis: { categories: rows.map((r) => r.shift_name) },
    tooltip: barPercentTooltip(),
  }
  return { seriesParams, chartOptionsParams }
}

export const formatAlarmReportChartParams = (chartId: string, readings: unknown[], compact = true) => {
  const chartFormatFunction = identifyAlarmReportChart(chartId)
  const chartParams = chartFormatFunction(readings)
  const isBar = isBarChartType(chartId)
  const isRose = isRoseChartType(chartId)
  let chartOptionsParams = applyCompactChartOptions(chartParams.chartOptionsParams as Record<string, unknown>, compact)

  if (!isBar && !isRose) {
    chartOptionsParams = applyDonutVisualOptions(chartOptionsParams, chartParams.seriesParams)
  }

  const chartHeight = compact ? (isBar ? COMPACT_BAR_HEIGHT : COMPACT_DONUT_HEIGHT) : undefined

  return {
    seriesParams: chartParams.seriesParams,
    chartOptionsParams,
    chartHeight,
    apexChartType: isBar ? 'bar' : isRose ? 'polarArea' : 'donut',
  }
}
